/**
 * Production Logger with External Monitoring Integration
 *
 * Supports:
 * - DataDog via HTTP API
 * - Sentry via SDK
 * - Custom webhook endpoints
 * - Logtail/Better Stack
 * - Structured JSON logging
 * - Per-request correlation IDs via AsyncLocalStorage
 * - PII/secret redaction on all meta payloads
 */

import { redact } from './redact'
import { getRequestContext } from './request-context'

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogContext {
  userId?: string
  requestId?: string
  path?: string
  method?: string
  environment?: string
  service?: string
  version?: string
  [key: string]: any
}

interface MonitoringConfig {
  datadog?: {
    apiKey: string
    site?: string // datadoghq.com or datadoghq.eu
    service?: string
    env?: string
  }
  sentry?: {
    dsn: string
  }
  logtail?: {
    sourceToken: string
  }
  webhook?: {
    url: string
    headers?: Record<string, string>
  }
}

// Buffer for batching logs in production
interface LogBuffer {
  logs: any[]
  flushTimeout: NodeJS.Timeout | null
}

class Logger {
  private context: LogContext = {}
  private config: MonitoringConfig = {}
  private buffer: LogBuffer = { logs: [], flushTimeout: null }
  private readonly BUFFER_SIZE = 100
  private readonly FLUSH_INTERVAL = 5000 // 5 seconds

  constructor() {
    this.loadConfig()

    // Set default context
    this.context = {
      environment: process.env.NODE_ENV || 'development',
      service: process.env.SERVICE_NAME || 'linkbox-web',
      version: process.env.APP_VERSION || '1.0.0',
    }
  }

  private loadConfig() {
    // Load from environment variables
    if (process.env.DATADOG_API_KEY) {
      this.config.datadog = {
        apiKey: process.env.DATADOG_API_KEY,
        site: process.env.DATADOG_SITE || 'datadoghq.com',
        service: process.env.SERVICE_NAME || 'linkbox-web',
        env: process.env.NODE_ENV || 'development',
      }
    }

    if (process.env.SENTRY_DSN) {
      this.config.sentry = {
        dsn: process.env.SENTRY_DSN,
      }
    }

    if (process.env.LOGTAIL_SOURCE_TOKEN) {
      this.config.logtail = {
        sourceToken: process.env.LOGTAIL_SOURCE_TOKEN,
      }
    }

    if (process.env.MONITORING_ENDPOINT) {
      this.config.webhook = {
        url: process.env.MONITORING_ENDPOINT,
        headers: process.env.MONITORING_HEADERS
          ? JSON.parse(process.env.MONITORING_HEADERS)
          : undefined,
      }
    }
  }

  setContext(context: LogContext) {
    this.context = { ...this.context, ...context }
  }

  clearContext() {
    const defaults = {
      environment: this.context.environment,
      service: this.context.service,
      version: this.context.version,
    }
    this.context = defaults
  }

  private log(level: LogLevel, message: string, meta?: any) {
    const timestamp = new Date().toISOString()

    // Pull correlation data from the per-request AsyncLocalStorage
    // store (set by `withSchema` or any entry-point wrapper). Safe
    // no-op outside a request scope — `getRequestContext()` returns
    // undefined and the spread degrades cleanly.
    const requestCtx = getRequestContext()
    const requestFields = requestCtx
      ? {
          request_id: requestCtx.requestId,
          ...(requestCtx.userId ? { user_id: requestCtx.userId } : {}),
          ...(requestCtx.path ? { path: requestCtx.path } : {}),
          ...(requestCtx.method ? { method: requestCtx.method } : {}),
          ...(requestCtx.meta || {}),
        }
      : {}

    // Redact before building the log record. Applying it to the
    // final payload would double-process context fields each call;
    // scrubbing just the user-supplied `meta` keeps the hot path
    // cheap while catching the shape that actually holds secrets
    // (tokens in request bodies, auth headers, vendor keys).
    const safeMeta = meta !== undefined ? redact(meta) : undefined

    const logData = {
      timestamp,
      level,
      message,
      ...this.context,
      ...requestFields,
      ...(safeMeta && typeof safeMeta === 'object' && !Array.isArray(safeMeta)
        ? (safeMeta as Record<string, unknown>)
        : safeMeta !== undefined
          ? { meta: safeMeta }
          : {}),
    }

    // Console logging with color in development
    if (process.env.NODE_ENV !== 'production') {
      const colors = {
        debug: '\x1b[36m', // cyan
        info: '\x1b[32m',  // green
        warn: '\x1b[33m',  // yellow
        error: '\x1b[31m', // red
      }
      const reset = '\x1b[0m'
      const prefix = requestCtx ? ` [${requestCtx.requestId}]` : ''
      console.log(`${colors[level]}[${level.toUpperCase()}]${reset}${prefix} ${message}`, safeMeta || '')
    } else {
      // Structured JSON in production
      if (level === 'error') {
        console.error(JSON.stringify(logData))
      } else if (level === 'warn') {
        console.warn(JSON.stringify(logData))
      } else {
        console.log(JSON.stringify(logData))
      }
    }

    try {
      // require('fs').appendFileSync('/tmp/linkbox-dev-debug.log', JSON.stringify(logData) + '\n');
    } catch(e) {}

    // Send to monitoring services in production
    if (process.env.NODE_ENV === 'production') {
      this.bufferLog(logData, level)
    }
  }

  private bufferLog(logData: any, level: LogLevel) {
    this.buffer.logs.push(logData)

    // Immediate flush for errors
    if (level === 'error') {
      this.flush()
      return
    }

    // Schedule flush if not already scheduled
    if (!this.buffer.flushTimeout && this.buffer.logs.length < this.BUFFER_SIZE) {
      this.buffer.flushTimeout = setTimeout(() => this.flush(), this.FLUSH_INTERVAL)
    }

    // Flush if buffer is full
    if (this.buffer.logs.length >= this.BUFFER_SIZE) {
      this.flush()
    }
  }

  private async flush() {
    if (this.buffer.logs.length === 0) return

    const logsToSend = [...this.buffer.logs]
    this.buffer.logs = []

    if (this.buffer.flushTimeout) {
      clearTimeout(this.buffer.flushTimeout)
      this.buffer.flushTimeout = null
    }

    // Send to all configured monitoring services in parallel
    const promises: Promise<void>[] = []

    if (this.config.datadog) {
      promises.push(this.sendToDatadog(logsToSend))
    }

    if (this.config.logtail) {
      promises.push(this.sendToLogtail(logsToSend))
    }

    if (this.config.webhook) {
      promises.push(this.sendToWebhook(logsToSend))
    }

    // Send errors to Sentry (handled separately in error method)

    try {
      await Promise.allSettled(promises)
    } catch (error) {
      // Fail silently to avoid infinite loops
      console.error('Failed to send logs to monitoring:', error)
    }
  }

  private async sendToDatadog(logs: any[]) {
    if (!this.config.datadog) return

    const { apiKey, site, service, env } = this.config.datadog

    // Format logs for Datadog
    const formattedLogs = logs.map(log => ({
      ddsource: 'nodejs',
      ddtags: `env:${env},service:${service}`,
      hostname: process.env.HOSTNAME || 'unknown',
      service,
      ...log,
    }))

    try {
      await fetch(`https://http-intake.logs.${site}/api/v2/logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'DD-API-KEY': apiKey,
        },
        body: JSON.stringify(formattedLogs),
      })
    } catch (error) {
      console.error('[Logger] DataDog send failed:', error)
    }
  }

  private async sendToLogtail(logs: any[]) {
    if (!this.config.logtail) return

    try {
      await fetch('https://in.logtail.com/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.logtail.sourceToken}`,
        },
        body: JSON.stringify(logs),
      })
    } catch (error) {
      console.error('[Logger] Logtail send failed:', error)
    }
  }

  private async sendToWebhook(logs: any[]) {
    if (!this.config.webhook) return

    try {
      await fetch(this.config.webhook.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.config.webhook.headers,
        },
        body: JSON.stringify({ logs }),
      })
    } catch (error) {
      console.error('[Logger] Webhook send failed:', error)
    }
  }

  private async sendToSentry(error: Error, context: any) {
    // Method name kept for backwards compatibility with existing
    // `.error()` callers across the codebase. Destination is now the
    // self-hosted `ingest-error-report` Edge Function (no third-party
    // vendor). See `lib/monitoring/supabase-error-sink.ts`.
    if (!this.config.sentry) return

    try {
      const { getSupabaseErrorSink } = await import('./supabase-error-sink')
      const sink = getSupabaseErrorSink()
      sink.captureException(error, {
        extra: {
          log_context: context,
          environment: this.context.environment || 'unknown',
          service: this.context.service || 'unknown',
          user_id: this.context.userId,
        },
      })
    } catch (err) {
      console.error('[Logger] Supabase error sink failed:', err)
    }
  }

  private formatArgs(args: any[]): { message: string, meta?: any } {
    if (args.length === 0) return { message: '' }

    const msg = typeof args[0] === 'string' ? args[0] : String(args[0])

    if (args.length === 1) return { message: msg }
    if (args.length === 2 && typeof args[1] === 'object' && args[1] !== null && !Array.isArray(args[1])) {
      return { message: msg, meta: args[1] }
    }

    return { message: msg, meta: { extra: args.slice(1) } }
  }

  debug(...args: any[]) {
    const { message, meta } = this.formatArgs(args)
    this.log('debug', message, meta)
  }

  info(...args: any[]) {
    const { message, meta } = this.formatArgs(args)
    this.log('info', message, meta)
  }

  warn(...args: any[]) {
    const { message, meta } = this.formatArgs(args)
    this.log('warn', message, meta)
  }

  error(...args: any[]) {
    if (args.length === 0) return

    const message = typeof args[0] === 'string' ? args[0] : String(args[0])
    let error: any = null
    let meta: any = null

    if (args.length > 1) {
      error = args[1]
      if (args.length > 2) {
        if (args.length === 3 && typeof args[2] === 'object' && !Array.isArray(args[2])) {
          meta = args[2]
        } else {
          meta = { extra: args.slice(2) }
        }
      }
    }

    const errorInfo = error instanceof Error ? {
      name: error.name,
      message: error.message,
      stack: error.stack,
    } : error

    const context = { ...meta, error: errorInfo }

    this.log('error', message, context)

    // Send to Sentry if configured and it's an actual Error
    if (error instanceof Error && process.env.NODE_ENV === 'production') {
      this.sendToSentry(error, context)
    }
  }

  // Helper for timing operations
  time(label: string): () => void {
    const start = performance.now()
    return () => {
      const duration = performance.now() - start
      this.info(`${label} completed`, { duration_ms: Math.round(duration) })
    }
  }

  // Helper for request logging
  request(req: { method: string; url: string; headers?: any }, res?: { statusCode: number }, duration?: number) {
    this.info('HTTP Request', {
      method: req.method,
      url: req.url,
      status: res?.statusCode,
      duration_ms: duration,
      userAgent: req.headers?.['user-agent'],
    })
  }
}

export const logger = new Logger()

/**
 * Create a module-scoped logger instance.
 * Usage: const log = createLogger('ai-chat')
 *        log.info('Processing query', { userId })
 */
export function createLogger(module: string) {
  const moduleLogger = new Logger()
  moduleLogger.setContext({ module })
  return moduleLogger
}
